/**
 * @fileoverview Connect JAMS processing history and attorney integration configuration to explicit credential/test actions. The backend owns secrets, provider communication, and document-delivery outcomes. Browsing history, changing configuration, testing connectivity, and processing provider messages remain distinct operations.
 */

import React, { useCallback, useEffect, useState, useContext } from 'react';
import { AppDataTable, AppColumn } from '../shared/AppDataTable';
import { AppButton, AppTextInput, AppTag, AppDropdown, AppSpinner } from '../shared';
import api from '../../api';
import ToastContext from '../../ToastContext';

const STATUS_OPTIONS = [
    { label: 'All', value: null },
    { label: 'Pending', value: 'pending' },
    { label: 'Downloaded', value: 'downloaded' },
    { label: 'Delivered', value: 'delivered' },
    { label: 'Failed', value: 'failed' },
];



function getStatusSeverity(status) {
    switch (status) {
        case 'delivered': return 'success';
        case 'downloaded': return 'info';
        case 'pending': return 'warning';
        case 'failed': return 'danger';
        default: return null;
    }
}



function JamsDocuments() {
    const [attorneys, setAttorneys] = useState([]);
    const [attorneysLoading, setAttorneysLoading] = useState(false);
    const [editingCredentials, setEditingCredentials] = useState({});
    const [savingId, setSavingId] = useState(null);
    const [testingId, setTestingId] = useState(null);

    const [records, setRecords] = useState([]);
    const [recordsLoading, setRecordsLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState(null);
    const [attorneyFilter, setAttorneyFilter] = useState(null);

    const showToast = useContext(ToastContext);

    const fetchAttorneys = useCallback(

    async () => {
        setAttorneysLoading(true);
        try {
            const response = await api.get('/jams/attorneys');
            setAttorneys(response.data.attorneys || []);
        } catch (error) {
            console.error('Error fetching JAMS attorneys:', error);
            showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load attorneys', life: 3000 });
        } finally {
            setAttorneysLoading(false);
        }
    }, [showToast]);

    const fetchRecords = useCallback(

    async () => {
        setRecordsLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (attorneyFilter) params.attorney_id = attorneyFilter;
            const response = await api.get('/jams/processed', { params });
            setRecords(response.data.records || []);
        } catch (error) {
            console.error('Error fetching JAMS records:', error);
            showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load processed emails', life: 3000 });
        } finally {
            setRecordsLoading(false);
        }
    }, [statusFilter, attorneyFilter, showToast]);

    useEffect(() => { fetchAttorneys(); }, [fetchAttorneys]);
    useEffect(() => { fetchRecords(); }, [fetchRecords]);

    const handleCredentialChange = useCallback(

    (attorneyId, field, value) => {
        setEditingCredentials(prev => ({
            ...prev,
            [attorneyId]: { ...prev[attorneyId], [field]: value },
        }));
    }, []);

    const handleSaveCredentials = useCallback(

    async (attorneyId) => {
        const creds = editingCredentials[attorneyId];
        if (!creds?.password) {
            showToast({ severity: 'warn', summary: 'Missing Fields', detail: 'Password is required', life: 3000 });
            return;
        }

        setSavingId(attorneyId);
        try {
            await api.post(`/jams/attorneys/${attorneyId}/credentials`, {
                password: creds.password,
            });
            showToast({ severity: 'success', summary: 'Saved', detail: 'JAMS credentials saved', life: 3000 });
            setEditingCredentials(prev => {
                const next = { ...prev };
                delete next[attorneyId];
                return next;
            });
            fetchAttorneys();
        } catch (error) {
            showToast({ severity: 'error', summary: 'Error', detail: 'Failed to save credentials', life: 3000 });
        } finally {
            setSavingId(null);
        }
    }, [editingCredentials, fetchAttorneys, showToast]);

    const handleDeleteCredentials = useCallback(

    (attorneyId, attorneyName) => {
        if (!window.confirm(`Remove JAMS credentials for ${attorneyName}?`)) return;
        (async () => {
            try {
                await api.delete(`/jams/attorneys/${attorneyId}/credentials`);
                showToast({ severity: 'success', summary: 'Removed', detail: 'JAMS credentials cleared', life: 3000 });
                fetchAttorneys();
            } catch (error) {
                showToast({ severity: 'error', summary: 'Error', detail: 'Failed to remove credentials', life: 3000 });
            }
        })();
    }, [fetchAttorneys, showToast]);

    const handleTestLogin = useCallback(

    async (attorneyId) => {
        setTestingId(attorneyId);
        try {
            const response = await api.post(`/jams/attorneys/${attorneyId}/test`);
            if (response.data?.success) {
                showToast({ severity: 'success', summary: 'Success', detail: 'JAMS login test passed', life: 3000 });
            } else {
                showToast({ severity: 'error', summary: 'Failed', detail: response.data?.message || 'Login test failed', life: 5000 });
            }
        } catch (error) {
            const msg = error.response?.data?.message || 'Login test failed';
            showToast({ severity: 'error', summary: 'Failed', detail: msg, life: 5000 });
        } finally {
            setTestingId(null);
        }
    }, [showToast]);

    const renderCredentialsEditor = useCallback(

    (rowData) => {
        const editing = editingCredentials[rowData.id];

        if (rowData.has_credentials && !editing) {
            return <AppTag severity="success" value="Configured" />;
        }

        return (
            <AppTextInput
                type="password"
                toggleMask
                placeholder="JAMS Password"
                value={editing?.password || ''}
                onChange={(e) => handleCredentialChange(rowData.id, 'password', e.target.value)}
                style={{ width: '200px' }}
            />
        );
    }, [editingCredentials, handleCredentialChange]);

    const renderActions = useCallback(

    (rowData) => {
        const editing = editingCredentials[rowData.id];
        const isSaving = savingId === rowData.id;
        const isTesting = testingId === rowData.id;

        return (
            <div className="flex align-items-center gap-1">
                {(editing || !rowData.has_credentials) && (
                    <AppButton
                        icon="pi pi-save"
                        variant="success"
                        onClick={() => handleSaveCredentials(rowData.id)}
                        disabled={isSaving}
                    />
                )}
                {rowData.has_credentials && !editing && (
                    <>
                        <AppButton
                            icon="pi pi-pencil"
                            variant="secondary"
                            onClick={() => handleCredentialChange(rowData.id, 'password', '')}
                        />
                        <AppButton
                            icon="pi pi-play"
                            variant="info"
                            onClick={() => handleTestLogin(rowData.id)}
                            disabled={isTesting}
                        />
                        <AppButton
                            icon="pi pi-trash"
                            variant="danger"
                            onClick={() => handleDeleteCredentials(rowData.id, rowData.Name)}
                        />
                    </>
                )}
            </div>
        );
    }, [editingCredentials, savingId, testingId, handleSaveCredentials, handleCredentialChange, handleTestLogin, handleDeleteCredentials]);

    const renderStatus = useCallback(

    (rowData) => (
        <AppTag value={rowData.status} severity={getStatusSeverity(rowData.status)} />
    ), []);

    const renderDate = useCallback(

    (rowData) => {
        const dt = rowData.created_at;
        if (!dt) return '-';
        return new Date(dt).toLocaleString();
    }, []);

    const renderProcessedDate = useCallback(

    (rowData) => {
        const dt = rowData.processed_at;
        if (!dt) return '-';
        return new Date(dt).toLocaleString();
    }, []);

    const attorneyFilterOptions = attorneys.map(a => ({ label: a.Name, value: a.id }));
    attorneyFilterOptions.unshift({ label: 'All Attorneys', value: null });

    return (
        <div className="jams-documents">
            {/* Section 1: Attorney Configuration */}
            <div className="surface-card p-4 mb-3 border-round">
                <div className="flex align-items-center justify-content-between mb-3">
                    <span className="text-xl font-medium">Attorney JAMS Configuration</span>
                    <AppButton
                        label="Refresh"
                        icon="pi pi-refresh"
                        variant="secondary"
                        onClick={fetchAttorneys}
                    />
                </div>

                {attorneysLoading && attorneys.length === 0 ? (
                    <div className="flex justify-content-center" style={{ minHeight: '150px' }}>
                        <AppSpinner />
                    </div>
                ) : (
                    <AppDataTable
                        value={attorneys}
                        responsiveLayout="scroll"
                        stripedRows
                        emptyMessage="No attorneys found"
                    >
                        <AppColumn field="Name" header="Attorney" sortable />
                        <AppColumn field="Email" header="Email" sortable />
                        <AppColumn header="Credentials" body={renderCredentialsEditor} style={{ minWidth: '220px' }} />
                        <AppColumn header="Actions" body={renderActions} style={{ width: '180px' }} />
                    </AppDataTable>
                )}
            </div>

            {/* Section 2: Processed Emails Log */}
            <div className="surface-card p-4 border-round">
                <div className="flex align-items-center justify-content-between mb-3">
                    <span className="text-xl font-medium">Processed Emails</span>
                    <div className="flex align-items-center gap-2">
                        <AppDropdown
                            value={statusFilter}
                            options={STATUS_OPTIONS}
                            onChange={(e) => setStatusFilter(e.value)}
                            placeholder="Filter by status"
                        />
                        <AppDropdown
                            value={attorneyFilter}
                            options={attorneyFilterOptions}
                            onChange={(e) => setAttorneyFilter(e.value)}
                            placeholder="Filter by attorney"
                        />
                        <AppButton
                            icon="pi pi-refresh"
                            variant="secondary"
                            onClick={fetchRecords}
                        />
                    </div>
                </div>

                {recordsLoading && records.length === 0 ? (
                    <div className="flex justify-content-center" style={{ minHeight: '150px' }}>
                        <AppSpinner />
                    </div>
                ) : (
                    <AppDataTable
                        value={records}
                        paginator
                        rows={25}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        responsiveLayout="scroll"
                        stripedRows
                        sortField="created_at"
                        sortOrder={-1}
                        emptyMessage="No processed emails found"
                    >
                        <AppColumn field="attorney_name" header="Attorney" sortable />
                        <AppColumn header="Status" body={renderStatus} sortable sortField="status" style={{ width: '120px' }} />
                        <AppColumn field="jams_file_url" header="File URL" style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }} />
                        <AppColumn header="Created" body={renderDate} sortable sortField="created_at" />
                        <AppColumn header="Processed" body={renderProcessedDate} sortable sortField="processed_at" />
                        <AppColumn field="error_message" header="Error" style={{ maxWidth: '200px' }} />
                    </AppDataTable>
                )}
            </div>
        </div>
    );
}

export default JamsDocuments;
